import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Paper } from "@mui/material";
import { ScanTrackSummaryDataComponents } from "./scan-track-summary-data.components";
import SnSummaryContainer from "../sn-summary/sn-summary.container";
import StockoutSummary from "../stockout-summary";
import {
  getMonthOptions,
  getYearOptions,
  getDateRange,
} from "../../utils/date-utils";
import { sanitizeFilters } from "../../utils/filter-utils";
import { TabMenuPanel } from "../qr-credit-summary/company-tab-menu";
import {
  fetchCompanyList,
  fetchBranchList,
  fetchProductList,
  fetchSummaryData,
  fetchAdditionalInfoFields,
  exportData,
} from "../../redux/scan-track-summary-data";

import {
  setPage,
  setItemPerPage,
  setFilters,
  resetAdditionalInfoFields,
} from "../../redux/scan-track-summary-data/scan-track-summary-data.slice";

export const ScanTrackSummaryDataContainer = () => {
  const dispatch = useDispatch();
  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();

  const [activeTab, setActiveTab] = useState("snScan");
  const [columnView, setColumnView] = useState({
    company: true,
    scanDateTime: true,
    serialNumber: true,
    branch: true,
    product: true,
    ipAddress: false,
    status: true,
    location: false,
    googleMapLink: false,
    latitudeLongitude: false,
    os: false,
    browser: false,
    deviceType: false,
    scanCount: false,
    scanLimit: false,
  });

  // Additional info column selection (uuid -> boolean)
  const [additionalInfoColumnView, setAdditionalInfoColumnView] = useState({});
  // Language selection for export headers
  const [exportLang, setExportLang] = useState("EN");

  const {
    data,
    companies,
    branches,
    products,
    additionalInfoFields,
    additionalInfoMeta,
    loading,
    error,
    filters,
    isExporting,
    page,
    itemPerPage,
    totalCount,
  } = useSelector((state) => state.scanTrackSummary);

  const [exportingType, setExportingType] = useState(null);
  const hasInitRef = useRef(false);
  const isDataFetchingRef = useRef(false);

  const formatDate = (date) =>
    date instanceof Date ? date.toISOString().split("T")[0] : date;

  // Get selected additional info column UUIDs
  const getSelectedAdditionalInfoColumns = () =>
    Object.keys(additionalInfoColumnView).filter(
      (uuid) => additionalInfoColumnView[uuid]
    );

  // Function to fetch data when needed
  const fetchDataIfNeeded = (filterValues, pageNum, perPage, adtInfoColumns) => {
    if (!isDataFetchingRef.current) {
      isDataFetchingRef.current = true;
      dispatch(
        fetchSummaryData({
          filters: sanitizeFilters(filterValues),
          page: pageNum,
          itemPerPage: perPage,
          additionalInfoColumns: adtInfoColumns || [],
          displayLang: exportLang,
        })
      ).finally(() => {
        isDataFetchingRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (activeTab === "scanTrack" && !hasInitRef.current) {
      dispatch(fetchCompanyList());
      hasInitRef.current = true;
    }
  }, [activeTab, dispatch]);

  // Only this useEffect will handle data fetching
  useEffect(() => {
    if (activeTab === "scanTrack" && filters.company) {
      const selectedAdtInfo = getSelectedAdditionalInfoColumns();
      fetchDataIfNeeded(filters, page, itemPerPage, selectedAdtInfo);
    }
  }, [activeTab, filters, page, itemPerPage, additionalInfoColumnView, exportLang, dispatch]);

  const handleFilterChange = (field, value) => {
    const updatedFilters = {
      ...filters,
      [field]: value,
    };

    if (field === "company") {
      if (value && activeTab === "scanTrack") {
        dispatch(fetchBranchList(value));
        dispatch(fetchProductList(value));
        dispatch(fetchAdditionalInfoFields(value));
      } else {
        dispatch(resetAdditionalInfoFields());
      }
      updatedFilters.branch = "ALL";
      updatedFilters.product = "ALL";
      // Reset additional info column selection when company changes
      setAdditionalInfoColumnView({});
    }

    updatedFilters.startDate = formatDate(updatedFilters.startDate);
    updatedFilters.endDate = formatDate(updatedFilters.endDate);

    dispatch(setFilters(updatedFilters));
    dispatch(setPage(0));
    // The useEffect will handle the data fetching
  };

  const columnKeyMap = {
    company: "company_name",
    branch: "branch_name",
    product: "product_name",
    ipAddress: "ip_address",
    location: "location",
    googleMapLink: "google_map_link",
    latitudeLongitude: ["latitude", "longitude"],
    os: "os",
    browser: "browser",
    deviceType: "device_type",
    scanCount: "scan_count",
    scanLimit: "scan_limit",
    serialNumber: "serial_number",
    productUuid: "product_uuid",
    status: "status",
    scanDateTime: "scan_date_time",
    branchId: "branch_id",
  };

  const handleExport = async (format) => {
    setExportingType(format);
    try {
      const selectedColumns = Object.keys(columnView)
        .filter((key) => columnView[key])
        .flatMap((key) => columnKeyMap[key] || []);
      const selectedCompany = companies.find(
        (c) => c.value === filters.company
      );
      const { label: companyName = "" } = selectedCompany || {};

      // Get selected additional info column UUIDs
      const selectedAdditionalInfoColumns = Object.keys(additionalInfoColumnView)
        .filter((uuid) => additionalInfoColumnView[uuid]);

      // Always build exportFilters with only the required fields
      let { startDate, endDate } = filters;
      if (filters.filterMode === "By Year" && filters.year) {
        // Use getDateRange utility to get full year range
        ({ startDate, endDate } = getDateRange({ year: filters.year }));
      } else if (
        filters.filterMode === "By Month" &&
        filters.year &&
        filters.month
      ) {
        ({ startDate, endDate } = getDateRange({
          year: filters.year,
          month: filters.month,
        }));
      }
      const exportFilters = {
        company: filters.company,
        branch: filters.branch,
        product: filters.product,
        filterMode: filters.filterMode,
        isDateRangeValid: filters.isDateRangeValid,
        startDate,
        endDate,
        // add any other fields you want to send
      };
      await dispatch(
        exportData({
          format,
          filters: sanitizeFilters(exportFilters),
          columns: selectedColumns,
          companyName,
          additionalInfoColumns: selectedAdditionalInfoColumns,
          displayLang: exportLang,
        })
      ).unwrap();
    } finally {
      setExportingType(null);
    }
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleItemPerPageChange = (newItemPerPage) => {
    dispatch(setItemPerPage(newItemPerPage));
    dispatch(setPage(0));
  };

  const menuItems = [
    { id: "snScan", label: "SN Scan Summary" },
    { id: "scanTrack", label: "Scan Track Summary" },
    { id: "stockout", label: "Stockout Summary" },
  ];

  return (
    <Box sx={{ display: "flex", gap: 2, p: 2 }}>
      <TabMenuPanel
        items={menuItems}
        activeId={activeTab}
        onTabChange={setActiveTab}
      />

      <Paper elevation={3} sx={{ flex: 1, p: 0, overflow: "auto" }}>
        {activeTab === "scanTrack" && (
          <ScanTrackSummaryDataComponents
            itemPerPage={itemPerPage}
            page={page}
            totalCount={totalCount}
            handlePageChange={handlePageChange}
            handleItemPerPageChange={handleItemPerPageChange}
            loading={loading}
            filterData={filters}
            onFilterChange={handleFilterChange}
            yearOptions={yearOptions}
            monthOptions={monthOptions}
            columnView={columnView}
            setColumnView={setColumnView}
            data={data}
            isScanTrackerLoading={loading.data}
            companyOptions={companies}
            error={error.data}
            handleExport={handleExport}
            isExporting={isExporting}
            exportingType={exportingType}
            branches={branches}
            products={products}
            isLoadingBranches={loading.branches}
            isLoadingProducts={loading.products}
            isLoadingFilterMode={loading.filterMode}
            branchError={error.branches}
            productError={error.products}
            additionalInfoFields={additionalInfoFields}
            additionalInfoColumnView={additionalInfoColumnView}
            setAdditionalInfoColumnView={setAdditionalInfoColumnView}
            loadingAdditionalInfoFields={loading.additionalInfoFields}
            exportLang={exportLang}
            setExportLang={setExportLang}
            additionalInfoMeta={additionalInfoMeta}
          />
        )}
        {activeTab === "snScan" && <SnSummaryContainer />}
        {activeTab === "stockout" && <StockoutSummary />}
      </Paper>
    </Box>
  );
};
